import React, { useEffect, useState, FC, Fragment } from 'react';

import { grayDark, baseMain } from 'crehana-ui/styles/colors';
import Dialog from 'crehana-ui/Dialog';

import Facebook from 'crehana-icons/Facebook';
import Google from 'crehana-icons/Google';
import FacebookLogo from 'crehana-icons/FacebookLogo';
import GoogleIcon from 'crehana-icons/GoogleIcon';

import { TextFieldProps } from 'crehana-ui/TextField/types';

import SignUpForm from 'Jsx/Auth/SignUp';
import EVENTS from 'Jsx/Auth/constants/Tracking';
import { useSocialAuth } from 'Jsx/Auth/components/SocialButtons/react-hooks';
import BasicDataForm from 'Jsx/Auth/components/BasicDataForm';

import mixpanel from 'Jsx/Utils/mixpanelTracking';

type CustomSignupFormProps = {
  isDesktop?: boolean;
  dark?: boolean;
  socialButtonText?: string;
  registerButtonLabel?: string;
  textFieldProps?: TextFieldProps;
  /**
   * Pass the page, section or component where this component is used.
   * This prop is used to send extra data to the mixpanel track "AUTH_REGISTER"
   * Ex: If you pass "catalog" the mixpanel track is sent as: `mixpanel(REGISTER, { isRenderedIn })`
   */
  isRenderedIn: string;
  buttonProps?: {
    id?: string;
  };
  sendMixpanelTrackAtFirstRender?: boolean;
  isCourseDetail?: boolean;
  legalsCourse?: string;
  triggerSOFE?: boolean;
  courseEnrollmentUrl?: string;
};

const CustomSignupForm: FC<CustomSignupFormProps> = ({
  isDesktop,
  dark,
  socialButtonText,
  registerButtonLabel,
  className,
  textFieldProps,
  isRenderedIn,
  buttonProps,
  sendMixpanelTrackAtFirstRender,
  isCourseDetail,
  legalsCourse,
  triggerSOFE,
  courseEnrollmentUrl,
}) => {
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);

  const redirectTo = (url: string) => {
    window.location.href = url;
  };

  const onSuccess = ({
    redirect,
    isNewUser,
  }: {
    redirect?: string;
    isNewUser?: boolean;
  }) => {
    if (triggerSOFE && isNewUser) {
      mixpanel.track(EVENTS.SIGNUP_FORM_BUTTON_CLICK, { isRenderedIn }, () => {
        redirectTo('/registro-extra/');
      });
    } else if (isCourseDetail && isNewUser) {
      mixpanel.track(EVENTS.SIGNUP_FORM_BUTTON_CLICK, { isRenderedIn }, () => {
        redirectTo(`/registro-extra/?enrollmenturl=${courseEnrollmentUrl}`);
      });
    } else if (redirect) {
      const url = redirect || '/home/';

      mixpanel.track(EVENTS.SIGNUP_FORM_BUTTON_CLICK, { isRenderedIn }, () => {
        redirectTo(url);
      });
    } else {
      setPhoneDialogOpen(true);
    }
  };

  const {
    authState: { loadingFacebookAndGoogleScripts },
    facebookButtonProps,
    gmailButtonProps,
    setupAndInitSocialAuthScripts,
  } = useSocialAuth({
    onSuccess,
    isRenderedIn,
  });

  useEffect(() => {
    setupAndInitSocialAuthScripts();
    if (sendMixpanelTrackAtFirstRender) {
      mixpanel.track(`${isRenderedIn}: Show Custom Signup Form`);
    }
  }, []);

  const textColorClassName = dark ? 'text-white' : '';
  const baseIconColor = dark ? grayDark : baseMain;

  return (
    <Fragment>
      <SignUpForm
        darkMode={dark}
        customHandler={onSuccess}
        customRegisterLabel={registerButtonLabel || 'Regístrate gratis'}
        textFieldProps={textFieldProps}
        isRenderedIn={isRenderedIn}
        buttonProps={buttonProps}
        renderFooter={({ button, legalCheckbox, getFormValues }) => {
          const isCheckboxLegalSelected = getFormValues('checkboxLegal');

          const iconColor = isCheckboxLegalSelected ? '#fff' : baseIconColor;

          return (
            <React.Fragment>
              <div className="mb-20">
                {button}
                {isCourseDetail && legalsCourse && (
                  <p className="font-legals pt-8 text-gray-dark text-center">
                    {legalsCourse}
                  </p>
                )}
              </div>
              <p
                className={`flex flex-col flex-wrap items-center mb-24 sm:mb-16 md:mb-24 text-center ${textColorClassName} ${isCourseDetail &&
                  'font-bold'} font-body3 md:flex-row md:justify-center`}
              >
                {socialButtonText || 'O regístrate con'}
                <span className="flex flex-shrink-0 mt-12 md:mt-0">
                  <button
                    type="button"
                    className="flex items-center ml-8"
                    disabled={
                      !isCheckboxLegalSelected ||
                      loadingFacebookAndGoogleScripts
                    }
                    style={{
                      cursor: loadingFacebookAndGoogleScripts
                        ? 'wait'
                        : 'pointer',
                    }}
                    {...gmailButtonProps}
                  >
                    {isCourseDetail ? (
                      <GoogleIcon size={26} />
                    ) : (
                      <React.Fragment>
                        <Google color={iconColor} size={20} />
                        <span className="inline-block ml-8 font-label">
                          GOOGLE
                        </span>
                      </React.Fragment>
                    )}
                  </button>
                  <span className="inline-block mx-12">o</span>
                  <button
                    type="button"
                    className="flex items-center"
                    disabled={
                      !isCheckboxLegalSelected ||
                      loadingFacebookAndGoogleScripts
                    }
                    {...facebookButtonProps}
                  >
                    {isCourseDetail ? (
                      <FacebookLogo color="#1877F2" size={26} />
                    ) : (
                      <React.Fragment>
                        <Facebook color={iconColor} size={20} />
                        <span className="inline-block ml-8 font-label">
                          FACEBOOK
                        </span>
                      </React.Fragment>
                    )}
                  </button>
                </span>
              </p>
              <div className="text-center">{legalCheckbox}</div>
            </React.Fragment>
          );
        }}
      />
      <Dialog
        size={isDesktop ? 's' : 'full'}
        open={phoneDialogOpen}
        onDismiss={() => setPhoneDialogOpen(false)}
        hideCloseBtn
        ignoreBackdropClick
        ignoreEscape
      >
        <div className="p-16 pt-56 md:py-56 md:px-48">
          <BasicDataForm
            phoneNumberOptions={{
              isRequired: true,
            }}
          />
        </div>
      </Dialog>
    </Fragment>
  );
};

export default CustomSignupForm;
